import { useState, useEffect, useCallback, useRef } from 'react';
import { Empty, PullToRefresh, InfiniteScroll, DotLoading } from 'antd-mobile';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { supabase, DRIVER_VISIBLE_TRIP_OR_FILTER } from '@ubus/shared';
import type { Trip } from '@ubus/shared';
import TaskCard from '../components/TaskCard';
import PageHeader from '../components/PageHeader';
import ReminderHeaderBell from '../components/ReminderHeaderBell';

const PAGE_SIZE = 10;

function TripHistory() {
  const { t } = useTranslation();
  const { driverId } = useAuth();
  const navigate = useNavigate();
  const [trips, setTrips] = useState<Trip[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [tab, setTab] = useState<'current' | 'history'>('current');
  const [currentCount, setCurrentCount] = useState(0);
  const [historyCount, setHistoryCount] = useState(0);
  const pageRef = useRef(0);

  const fetchCounts = useCallback(async () => {
    if (!driverId) return;

    const [currentRes, historyRes] = await Promise.all([
      supabase
        .from('trips')
        .select('id', { count: 'exact', head: true })
        .eq('driver_id', driverId)
        .or(DRIVER_VISIBLE_TRIP_OR_FILTER)
        .in('status', ['scheduled', 'dispatched', 'in_progress']),
      supabase
        .from('trips')
        .select('id', { count: 'exact', head: true })
        .eq('driver_id', driverId)
        .or(DRIVER_VISIBLE_TRIP_OR_FILTER)
        .in('status', ['completed', 'cancelled']),
    ]);

    setCurrentCount(currentRes.count ?? 0);
    setHistoryCount(historyRes.count ?? 0);
  }, [driverId]);

  const fetchPage = useCallback(
    async (pageIndex: number, reset: boolean) => {
      if (!driverId) {
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        let query = supabase
          .from('trips')
          .select('*, vehicles(*), organizations(*)')
          .eq('driver_id', driverId)
          .or(DRIVER_VISIBLE_TRIP_OR_FILTER);

        if (tab === 'current') {
          query = query.in('status', ['scheduled', 'dispatched', 'in_progress']);
        } else {
          query = query.in('status', ['completed', 'cancelled']);
        }

        query = query
          .order('date', { ascending: false })
          .order('pickup_time', { ascending: false })
          .range(pageIndex * PAGE_SIZE, (pageIndex + 1) * PAGE_SIZE - 1);

        const { data, error } = await query;
        if (error) throw error;

        const batch = data || [];
        setHasMore(batch.length === PAGE_SIZE);

        if (reset) {
          setTrips(batch);
          pageRef.current = 1;
        } else {
          setTrips((prev) => [...prev, ...batch]);
          pageRef.current = pageIndex + 1;
        }
      } catch (err) {
        console.error('Failed to fetch tasks:', err);
      } finally {
        setLoading(false);
      }
    },
    [driverId, tab]
  );

  useEffect(() => {
    void fetchCounts();
  }, [fetchCounts, tab]);

  useEffect(() => {
    setTrips([]);
    setHasMore(true);
    pageRef.current = 0;
    fetchPage(0, true);
  }, [tab, driverId, fetchPage]);

  const loadMore = async () => {
    if (loading || !hasMore) return;
    await fetchPage(pageRef.current, false);
  };

  const segments = [
    { key: 'current' as const, label: t('myTasks.current'), count: currentCount },
    { key: 'history' as const, label: t('myTasks.history'), count: historyCount },
  ];

  const emptyText =
    tab === 'current' ? t('myTasks.emptyCurrent') : t('myTasks.emptyHistory');

  return (
    <div className="driver-mytask-page">
      <PageHeader
        title={t('myTasks.title')}
        subtitle={t('myTasks.subtitle')}
        right={<ReminderHeaderBell variant="onLight" />}
      />

      <div className="driver-section">
        <div className="driver-tabs-underline">
          {segments.map((seg) => (
            <button
              key={seg.key}
              type="button"
              className={`driver-tabs-underline__item ${tab === seg.key ? 'driver-tabs-underline__item--active' : ''}`}
              onClick={() => setTab(seg.key)}
            >
              {seg.label}
              <span className="driver-tabs-underline__count">({seg.count})</span>
            </button>
          ))}
        </div>

        <PullToRefresh onRefresh={() => { void fetchCounts(); return fetchPage(0, true); }}>
          {loading && trips.length === 0 ? (
            <div className="driver-page-loading">
              <DotLoading color="primary" />
            </div>
          ) : trips.length === 0 ? (
            <Empty description={emptyText} className="driver-page-empty" />
          ) : (
            trips.map((trip) => (
              <TaskCard
                key={trip.id}
                trip={trip}
                variant={tab === 'current' ? 'mytask-current' : 'mytask-history'}
                onClick={() => navigate(`/task/${trip.id}`)}
                onArrive={() => navigate(`/task/${trip.id}`)}
              />
            ))
          )}

          {tab === 'history' && trips.length > 0 && (
            <InfiniteScroll loadMore={loadMore} hasMore={hasMore} />
          )}
        </PullToRefresh>
      </div>
    </div>
  );
}

export default TripHistory;
